import React from "react";
import {Alert, Box, Chip, Link, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import {formatDataIsoBR, formatReais} from "../../../shared/utils/formatters";
import {getStatusColor, getStatusLabel} from "../../Licencas/licencasUtils";
import {getMeioDaCobrancaLabel, hojeIso, REGUA_PASSOS, textoDoProximoPasso} from "../cobrancasUtils";

const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

function ProximoPasso({linha, hoje}) {
    const passo = linha.proximoPasso;
    if (!passo) return <Typography variant="body2" color="text.secondary">Fim da régua</Typography>;
    const texto = textoDoProximoPasso(passo, hoje);
    return (
        <Box>
            <Typography variant="body2" fontWeight={600}>{getStatusLabel(passo.situacao)}</Typography>
            <Typography variant="caption" color={texto?.atrasado ? 'warning.main' : 'text.secondary'}>
                {texto?.atrasado ? `${formatDataIsoBR(passo.data)}, ${texto.quando}` : `em ${formatDataIsoBR(passo.data)}`}
            </Typography>
        </Box>
    );
}

/*
 * A régua de inadimplência (BBF-152): as assinaturas com cobrança vencida ou vencendo hoje, quantos dias de atraso e
 * o que acontece em seguida. Quem muda a situação é a execução diária do financeiro; quem regulariza é o pagamento.
 */
export default function ReguaCard({linhas = [], carregando = false, erro = '', onAbrirCobranca}) {
    const hoje = hojeIso();

    return (
        <Paper variant="outlined" sx={{borderRadius: 2.5, overflow: 'hidden'}}>
            <Box sx={{p: {xs: 2, md: 2.5}, borderBottom: 1, borderColor: 'divider'}}>
                <Typography fontWeight={700}>Régua de inadimplência</Typography>
                <Typography variant="body2" color="text.secondary">
                    Dias contados do vencimento da cobrança mais antiga em aberto. A régua só agrava; o pagamento regulariza.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{mt: 1.5}}>
                    {REGUA_PASSOS.map((passo) => (
                        // O texto quebra em vez de cortar com reticências: no celular a legenda não cabe numa linha.
                        <Chip key={passo.situacao} size="small" variant="outlined"
                              sx={{height: 'auto', maxWidth: '100%', '& .MuiChip-label': {whiteSpace: 'normal', py: 0.5}}}
                              label={`${getStatusLabel(passo.situacao)} · ${plural(passo.dias, 'dia', 'dias')} · ${passo.efeito}`} />
                    ))}
                </Stack>
            </Box>

            {erro ? <Alert severity="error" sx={{m: 2}}>{erro}</Alert> : null}
            {!erro && carregando ? <Typography color="text.secondary" sx={{p: 2.5}}>Carregando a régua...</Typography> : null}
            {!erro && !carregando && !linhas.length ? (
                <Typography color="text.secondary" sx={{p: 2.5}}>Nenhuma assinatura com cobrança vencida ou vencendo hoje.</Typography>
            ) : null}

            {!erro && linhas.length ? (
                <>
                    <Box sx={{display: {xs: 'none', md: 'block'}, overflowX: 'auto'}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Assinatura</TableCell>
                                    <TableCell>Cobrança mais atrasada</TableCell>
                                    <TableCell>Atraso</TableCell>
                                    <TableCell>Situação</TableCell>
                                    <TableCell>Próximo passo</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {linhas.map((linha) => (
                                    <TableRow key={linha.licencaId}>
                                        <TableCell>
                                            <Link component={RouterLink} to={`/licencas/${linha.licencaId}`} fontWeight={600}>{linha.empresaNome || '-'}</Link>
                                            <Typography variant="caption" display="block" color="text.secondary">{linha.planoNome || 'Assinatura'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Link component="button" type="button" variant="body2" onClick={() => onAbrirCobranca?.(linha.cobranca.id)}>
                                                {getMeioDaCobrancaLabel(linha.cobranca?.meio)} · {formatReais(linha.cobranca?.valor)}
                                            </Link>
                                            <Typography variant="caption" display="block" color="text.secondary">venceu em {formatDataIsoBR(linha.cobranca?.vencimento)}</Typography>
                                        </TableCell>
                                        <TableCell sx={{whiteSpace: 'nowrap'}}>
                                            <Typography variant="body2" fontWeight={600}>{linha.diasDeAtraso ? plural(linha.diasDeAtraso, 'dia', 'dias') : 'Vence hoje'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={getStatusLabel(linha.situacao)} color={getStatusColor(linha.situacao)} variant="outlined" />
                                        </TableCell>
                                        <TableCell><ProximoPasso linha={linha} hoje={hoje} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                    <Stack spacing={0} sx={{display: {xs: 'flex', md: 'none'}}} divider={<Box sx={{borderTop: 1, borderColor: 'divider'}} />}>
                        {linhas.map((linha) => (
                            <Stack key={linha.licencaId} spacing={1} sx={{p: 2}}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Box sx={{minWidth: 0}}>
                                        <Link component={RouterLink} to={`/licencas/${linha.licencaId}`} fontWeight={600}>{linha.empresaNome || '-'}</Link>
                                        <Typography variant="caption" display="block" color="text.secondary">{linha.planoNome || 'Assinatura'}</Typography>
                                    </Box>
                                    <Chip size="small" label={getStatusLabel(linha.situacao)} color={getStatusColor(linha.situacao)} variant="outlined" />
                                </Stack>
                                <Typography variant="body2">
                                    {linha.diasDeAtraso ? plural(linha.diasDeAtraso, 'dia', 'dias') : 'Vence hoje'} ·{' '}
                                    <Link component="button" type="button" variant="body2" onClick={() => onAbrirCobranca?.(linha.cobranca.id)}>
                                        {getMeioDaCobrancaLabel(linha.cobranca?.meio)} de {formatReais(linha.cobranca?.valor)}
                                    </Link>
                                </Typography>
                                <ProximoPasso linha={linha} hoje={hoje} />
                            </Stack>
                        ))}
                    </Stack>
                </>
            ) : null}
        </Paper>
    );
}
